import React from 'react';

import { PartialProps } from 'typescriptUtilityTypes';

/**
 * Returns a nice displayName for React Dev Tools.
 * @param component - React FC
 */
function getDevToolName(Component: React.JSXElementConstructor<any>) {
  // eslint-disable-next-line i18next/no-literal-string
  const displayName = (Component as any).displayName || Component.name || 'Component';
  return `Injected(${displayName})`;
}

/**
 * HOC. Injects static values to a component so that they're always provided.
 * @example
 * const InjectedComponent = injectProps<ComponentProps, 'compProp1' | 'compProp2'>(
 *   Component,
 *   { compProp1: 'value', compProp2: 'value' }
 * );
 * @param Component
 * @param injector - injected props
 * @returns component with injected properties
 */
export function injectProps<TProps, TInjectedKeys extends keyof TProps>(
  Component: React.JSXElementConstructor<TProps>,
  injector: Pick<TProps, TInjectedKeys>,
) {
  function Injected(props: TProps) {
    return <Component {...injector} {...(props as TProps)} />;
  }
  Injected.displayName = getDevToolName(Component);
  return Injected;
}

/**
 * HOC. Injects static values to a component so that they're always provided.
 * Then all these properties would be excluded from the component props
 * @example
 * const InjectedComponent = injectPropsAndExclude<ComponentProps, 'compProp1' | 'compProp2'>(
 *   Component,
 *   { compProp1: 'value', compProp2: 'value' }
 * );
 * @param Component
 * @param injector - injected props
 * @returns component with injected properties
 */
export function injectPropsAndExclude<TProps, TInjectedKeys extends keyof TProps>(
  Component: React.JSXElementConstructor<TProps>,
  injector: Pick<TProps, TInjectedKeys>,
) {
  function Injected(props: Omit<TProps, TInjectedKeys>) {
    return <Component {...(props as TProps)} {...injector} />;
  }
  Injected.displayName = getDevToolName(Component);
  return Injected;
}

/**
 * HOC. Injects static values to a component so that they're always provided
 * Then all these properties would be make optional
 * @example
 * const InjectedComponent = injectPropsAndMakeOptional<ComponentProps, 'compProp1' | 'compProp2'>(
 *   Component,
 *   { compProp1: 'value', compProp2: 'value' }
 * );
 * @param Component
 * @param injector - injected props
 * @returns component with injected properties
 */
export function injectPropsAndMakeOptional<TProps, TInjectedKeys extends keyof TProps>(
  Component: React.JSXElementConstructor<TProps>,
  injector: Pick<TProps, TInjectedKeys>,
) {
  function Injected(props: PartialProps<TProps, TInjectedKeys>) {
    return <Component {...injector} {...(props as TProps)} />;
  }
  Injected.displayName = getDevToolName(Component);
  return Injected;
}
